import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdRefresh, MdSchedule, MdCheckCircle, MdCancel } from 'react-icons/md';
import { getMyMoveOutRequests } from '../../../api/services/rentalService';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './MoveOutRequests.css';

const STATUS_TABS = [
  { id: 'PENDING', label: 'Pending', icon: MdSchedule },
  { id: 'APPROVED', label: 'Accepted', icon: MdCheckCircle },
  { id: 'REJECTED', label: 'Rejected', icon: MdCancel },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMonthYear = (month, year) => {
  const m = Number(month);
  const y = Number(year);
  if (!Number.isFinite(m) || !Number.isFinite(y)) return '—';
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const MoveOutRequests = () => {
  const [activeStatus, setActiveStatus] = useState('PENDING');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (status) => {
    try {
      setLoading(true);
      setError('');
      const res = await getMyMoveOutRequests(status);
      const list = Array.isArray(res?.data) ? res.data : [];
      const sorted = [...list].sort((a, b) => {
        const ta = new Date(a.createdAt || 0).getTime();
        const tb = new Date(b.createdAt || 0).getTime();
        return tb - ta;
      });
      setRows(sorted);
    } catch (e) {
      setRows([]);
      setError(e?.message || 'Could not load move-out requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(activeStatus);
  }, [activeStatus, load]);

  const listTitle = useMemo(
    () => (
      activeStatus === 'PENDING'
        ? 'Pending move-out requests'
        : activeStatus === 'APPROVED'
          ? 'Accepted move-out requests'
          : 'Rejected move-out requests'
    ),
    [activeStatus]
  );

  return (
    <div className="move-out-requests-page">
      <CollapsibleTopPanel id="tenant-moveout-top" defaultOpenOnMobile={false}>
        <header className="move-out-requests-header">
          <div>
            <h1 className="move-out-requests-title">Move out requests</h1>
            <p className="move-out-requests-subtitle">Track your submitted move-out notices and their status.</p>
          </div>
          <button
            type="button"
            className="move-out-requests-refresh"
            onClick={() => load(activeStatus)}
            disabled={loading}
            aria-label="Refresh move out requests"
          >
            <MdRefresh className={loading ? 'spin' : ''} />
          </button>
        </header>
      </CollapsibleTopPanel>

      <div className="move-out-requests-tabs" role="tablist" aria-label="Move out status tabs">
        {STATUS_TABS.map((tab) => {
          const selected = activeStatus === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`move-out-requests-tab ${selected ? 'active' : ''}`}
              onClick={() => setActiveStatus(tab.id)}
            >
              <Icon />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <h2 className="move-out-requests-list-title">{listTitle}</h2>

      {loading ? <div className="move-out-requests-state">Loading requests...</div> : null}

      {!loading && error ? (
        <div className="move-out-requests-state move-out-requests-state--error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => load(activeStatus)}>
            Try again
          </button>
        </div>
      ) : null}

      {!loading && !error && rows.length === 0 ? (
        <div className="move-out-requests-state">
          No {activeStatus === 'PENDING' ? 'pending' : activeStatus === 'APPROVED' ? 'accepted' : 'rejected'} move-out requests yet.
        </div>
      ) : null}

      {!loading && !error && rows.length > 0 ? (
        <div className="move-out-requests-list">
          {rows.map((row) => (
            <article key={row.id} className="move-out-request-card">
              <div className="move-out-request-card-top">
                <h3>{row.propertyName || 'Property'}</h3>
                <span className={`move-out-status move-out-status--${String(row.noticeStatus || '').toLowerCase()}`}>
                  {String(row.noticeStatus || '—').replace(/_/g, ' ')}
                </span>
              </div>
              <p className="move-out-request-unit">{row.unitName || row.unitNumber || 'Unit'}</p>
              <dl className="move-out-request-meta">
                <div>
                  <dt>Requested</dt>
                  <dd>{formatDateTime(row.createdAt)}</dd>
                </div>
                <div>
                  <dt>Planned move out</dt>
                  <dd>{formatMonthYear(row.noticeMoveOutMonth, row.noticeMoveOutYear)}</dd>
                </div>
                <div>
                  <dt>Reason</dt>
                  <dd>{row.noticeReason || 'No reason'}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default MoveOutRequests;
